import { useState, type FormEvent } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";

interface FormData {
  name: string;
  email: string;
  company: string;
  stage: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const initialForm: FormData = {
  name: "",
  email: "",
  company: "",
  stage: "",
  message: "",
};

function validate(form: FormData): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.email.trim()) {
    errors.email = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Invalid email address";
  }
  if (!form.message.trim()) errors.message = "Message is required";
  return errors;
}

export function useContactForm(onSuccess?: () => void) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function updateField(field: keyof FormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function reset() {
    setForm(initialForm);
    setErrors({});
    setSubmitError(null);
    setIsSuccess(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        stage: form.stage || null,
        message: form.message.trim(),
        source: window.location.href,
      };

      if (!isSupabaseConfigured) {
        console.log("Lead capture (dev mode):", payload);
      } else {
        const { error } = await supabase.from("leads").insert(payload);
        if (error) throw error;

        // Fire-and-forget notification edge function
        supabase.functions
          .invoke("notify-lead", { body: payload })
          .catch((err: unknown) =>
            console.warn("notify-lead edge function failed:", err)
          );
      }

      trackEvent("form_submit", { status: "success" });
      setIsSuccess(true);
      setForm(initialForm);

      setTimeout(() => {
        setIsSuccess(false);
        onSuccess?.();
      }, 2000);
    } catch (err) {
      trackEvent("form_submit", { status: "error" });
      setSubmitError("Something went wrong. Please try again.");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    form,
    errors,
    isSubmitting,
    isSuccess,
    submitError,
    updateField,
    handleSubmit,
    reset,
  };
}
